/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Flag, { FlagGroup } from '@atlaskit/flag';
import { ErrorMessage } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import { token } from '@atlaskit/tokens';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useAgentsPaginatedActionsWithOptimistic } from '../../../controllers/agents/index.tsx';
import { useBrowseAgentsUIStore } from '../../../controllers/browse-agents-ui/index.tsx';

import messages from './messages.ts';

const styles = cssMap({
	flag: {
		zIndex: 600,
	},

	err: {
		marginTop: token('space.150'),
	},
});

type Props = {
	onClose?: () => void;
} & AssistanceServiceInputParam;

const AgentDeleteConfirm = ({ onClose, assistanceServiceParams }: Props): JSX.Element => {
	di(useAgentsPaginatedActionsWithOptimistic);

	const [
		{
			agentDeletion: { agent, status },
		},
		{ setAgentDeletion },
	] = useBrowseAgentsUIStore();

	const { formatMessage } = useIntl();

	const { deleteAgent } = useAgentsPaginatedActionsWithOptimistic({
		assistanceServiceParams,
	});

	const { trackAgentEvent } = useRovoAgentActionAnalytics({
		touchPoint: 'agentDeleteConfirm',
	});

	const deleteError = useMemo(() => {
		if (deleteAgent.error) {
			const errorCode = deleteAgent.error.errorAttributes.errorCode;

			if (errorCode && errorCode in messages) {
				return formatMessage(messages[errorCode as keyof typeof messages]);
			}

			return formatMessage(messages.error);
		}
	}, [deleteAgent.error, formatMessage]);

	const handleDeleteAgent = useCallback(async () => {
		if (agent?.id) {
			deleteAgent.mutate(agent.id, {
				onSuccess: () => {
					trackAgentEvent({
						actionSubject: 'rovoAgent',
						action: 'delete',
						attributes: { agentId: agent.id },
					});
					onClose?.();
					setAgentDeletion({ status: 'success', agent: agent });
				},
				onError: () => {
					onClose?.();
					setAgentDeletion({ status: 'error', agent: agent });
				},
			});
		}
	}, [deleteAgent, onClose, agent, setAgentDeletion, trackAgentEvent]);

	const handleCloseModal = useCallback(() => {
		onClose?.();
		setAgentDeletion({ status: 'none', agent: null });
	}, [onClose, setAgentDeletion]);

	const isOpen = agent && status === 'none';

	return (
		<ModalTransition>
			{isOpen && (
				<Modal width="small" onClose={handleCloseModal}>
					<ModalHeader hasCloseButton>
						<ModalTitle>{formatMessage(messages.title, { agentName: agent?.name })}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Text as="p">{formatMessage(messages.body)}</Text>
						{!!deleteError && (
							<Box xcss={styles.err}>
								<ErrorMessage>{deleteError}</ErrorMessage>
							</Box>
						)}
						<ModalFooter>
							<Button appearance="subtle" onClick={handleCloseModal}>
								{formatMessage(messages.cancelText)}
							</Button>
							<Button
								appearance="danger"
								isDisabled={!!deleteError || deleteAgent.isSubmitting}
								onClick={() => {
									handleDeleteAgent();
								}}
							>
								{formatMessage(messages.confirmText)}
							</Button>
						</ModalFooter>
					</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
};

export const AgentDeleteFlags = ({
	assistanceServiceParams,
}: AssistanceServiceInputParam): JSX.Element => {
	const { formatMessage } = useIntl();
	const [
		{
			agentDeletion: { agent, status },
		},
		{ setAgentDeletion },
	] = useBrowseAgentsUIStore();

	return (
		<Box xcss={styles.flag}>
			<FlagGroup shouldRenderToParent>
				<>
					{status === 'success' && (
						<Flag
							id="delete-success-flag"
							icon={
								<SuccessIcon
									spacing="spacious"
									label="success"
									color={token('color.icon.success')}
								/>
							}
							title={formatMessage(messages.agentDeletedSuccessFlagTitle)}
							description={formatMessage(messages.agentDeletedSuccessFlagDescription, {
								agentName: agent?.name,
							})}
							onDismissed={() =>
								setAgentDeletion({
									agent: null,
									status: 'none',
								})
							}
						/>
					)}
					{status === 'error' && (
						<Flag
							id="delete-error-flag"
							icon={
								<ErrorIcon spacing="spacious" label="error" color={token('color.icon.danger')} />
							}
							title={formatMessage(messages.agentDeletedErrorFlagTitle)}
							description={formatMessage(messages.agentDeletedErrorFlagDescription)}
							onDismissed={() =>
								setAgentDeletion({
									agent: null,
									status: 'none',
								})
							}
						/>
					)}
				</>
			</FlagGroup>
		</Box>
	);
};

export default AgentDeleteConfirm;
